import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Check, Clock, BarChart, ChevronDown, FileText, Loader2, Lock, Shield } from "lucide-react";
import { Container, Eyebrow, PageHeader, Section, SectionHeading, buttonClass } from "../components/site";
import { Reveal } from "../components/motion";
import { useI18n } from "../i18n";
import axios from "axios";
import getApiBaseUrl from "../utils/apiBaseUrl";

const BACKEND_URL = getApiBaseUrl();

const normalizePricing = (pricingData = {}) => {
  const asNumber = (value, fallback) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  return {
    base_price: asNumber(pricingData.base_price, 99),
    gst_percent: asNumber(pricingData.gst_percent, 18),
    gst_amount: asNumber(pricingData.gst_amount, 17.82),
    total_price: asNumber(pricingData.total_price, 116.82),
    currency: pricingData.currency || "INR",
  };
};

const formatPrice = (value) => {
  const parsed = Number(value);
  const safeValue = Number.isFinite(parsed) ? parsed : 0;

  return new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(safeValue);
};

const GuidePage = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    country: "India",
    gst_number: "",
    company_name: "",
    terms_accepted: false,
  });
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [selectedGuideTitle, setSelectedGuideTitle] = useState("Placement Success Blueprint");
  const formRef = useRef(null);

  useEffect(() => {
    // SEO meta tags
    document.title = "Guides | Optivra Tech";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute("content", "Explore our exclusive knowledge hub. Technical blueprints spanning ComfyUI, RAG Systems, AI Automation, and n8n scripts.");
    }

    // Fetch pricing
    axios
      .get(`${BACKEND_URL}/guide/pricing`)
      .then((res) => setPricing(normalizePricing(res.data)))
      .catch((err) => {
        console.error("[GuidePage] Pricing API failed", {
          endpoint: `${BACKEND_URL}/guide/pricing`,
          method: "GET",
          status: err.response?.status,
          statusText: err.response?.statusText,
          responseData: err.response?.data,
          errorMessage: err.message,
        });
        setPricing(normalizePricing());
      });
  }, []);

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleSelectGuide = (title) => {
    setSelectedGuideTitle(title);
    scrollToForm();
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Full name is required";
    if (!formData.email.trim()) newErrors.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
      newErrors.email = "Please enter a valid email";
    if (!formData.phone.trim()) newErrors.phone = "Phone number is required";
    else if (!/^[+]?[\d\s-]{8,15}$/.test(formData.phone))
      newErrors.phone = "Please enter a valid phone number";
    if (!formData.terms_accepted)
      newErrors.terms_accepted = "You must accept the terms";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    const endpoint = `${BACKEND_URL}/guide/create-order`;
    try {
      // NOTE: We do not modify payload since backend explicitly expects standard structural flow without strict modifications.
      const res = await axios.post(endpoint, formData);
      console.info("[GuidePage] Create order success", {
        endpoint,
        method: "POST",
        status: res.status,
        orderId: res.data?.order_id,
        hasRedirectUrl: Boolean(res.data?.phonepe_redirect_url),
      });
      if (res.data.phonepe_redirect_url) {
        window.location.href = res.data.phonepe_redirect_url;
      }
    } catch (err) {
      console.error("[GuidePage] Create order failed", {
        endpoint,
        method: "POST",
        status: err.response?.status,
        statusText: err.response?.statusText,
        responseData: err.response?.data,
        requestPayload: {
          ...formData,
          phone: formData.phone ? "[REDACTED]" : "",
          email: formData.email ? "[REDACTED]" : "",
        },
        errorMessage: err.message,
      });
      const msg =
        err.response?.data?.detail || "Something went wrong. Please try again.";
      setErrors({ submit: msg });
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const guides = [
    {
      id: "g0",
      title: "Placement Success Blueprint",
      shortDesc: "Complete 30-45 day action plan to land your dream job. Includes resume strategy, interview prep, and cold outreach templates.",
      readTime: "120 mins",
      difficulty: "All Levels",
      tags: ["Career Guides", "Strategy", "Job Search"]
    },
    {
      id: "g1",
      title: "ComfyUI Workflows Blueprint",
      shortDesc: "Master diffusion models, custom nodes, and rapid prototyping workflows for synthetic media generation.",
      readTime: "45 mins",
      difficulty: "Advanced",
      tags: ["Computer Vision", "Nodes", "Generative AI"]
    },
    {
      id: "g2",
      title: "AI Automation Guide",
      shortDesc: "The complete technical blueprint for creating autonomous systems that replace manual business labor.",
      readTime: "60 mins",
      difficulty: "Intermediate",
      tags: ["Process Automation", "Architecture", "Scaling"]
    },
    {
      id: "g3",
      title: "RAG Systems Handbook",
      shortDesc: "Vector databases, chunking strategies, and deploying contextual agents using corporate domains.",
      readTime: "50 mins",
      difficulty: "Advanced",
      tags: ["Embeddings", "LLMs", "Data Pipelines"]
    },
    {
      id: "g4",
      title: "n8n Automation Guide",
      shortDesc: "Setup, host, and script advanced multi-app logic paths utilizing webhook listeners and triggers.",
      readTime: "30 mins",
      difficulty: "Beginner",
      tags: ["n8n", "Webhooks", "Integrations"]
    }
  ];

  const { t } = useI18n();
  const faqs = t("guide.faqs");
  const guideErrors = t("guide.errors");
  const tErr = (msg) => (msg && guideErrors[msg]) || msg;
  const readMins = (readTime) => `${parseInt(readTime, 10)} ${t("guide.mins")}`;
  const level = (difficulty) => t("guide.levels")[difficulty] || difficulty;
  const optional = <span className="font-normal text-ink-3">({t("common.optional")})</span>;

  return (
    <>
      <Header />

      <PageHeader eyebrow={t("guide.eyebrow")} title={t("guide.title")} lede={t("guide.lede")}>
        <ul className="mt-8 flex flex-wrap gap-2">
          {t("guide.badges").map((b) => (
            <li key={b} className="inline-flex items-center gap-2 rounded-full border border-line bg-surface px-4 py-2 text-sm text-ink-2">
              <Check size={14} className="text-brand" /> {b}
            </li>
          ))}
        </ul>
      </PageHeader>

      {/* Guide list */}
      <Section>
        <SectionHeading label={t("guide.listLabel")} title={t("guide.listTitle")} />
        <div className="grid gap-4 md:grid-cols-2">
          {guides.map((guide, i) => {
            const selected = guide.title === selectedGuideTitle;
            return (
              <Reveal key={guide.id} delay={(i % 2) * 100}>
                <article
                  className={`card-lift flex h-full flex-col rounded-3xl border bg-surface p-7 ${
                    selected ? "border-brand ring-2 ring-brand/30" : "border-line"
                  }`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-soft text-brand">
                      <FileText size={20} />
                    </span>
                    {selected && (
                      <span className="pop-in inline-flex items-center gap-1.5 rounded-full bg-brand px-3 py-1 text-xs font-medium text-on-brand">
                        <Check size={12} strokeWidth={3} /> {t("guide.selected")}
                      </span>
                    )}
                  </div>
                  <h3 className="mt-6 text-2xl font-semibold tracking-tight text-ink">{guide.title}</h3>
                  <p className="mt-2 flex-grow text-[0.9375rem] leading-relaxed text-ink-2">
                    {t(`guide.items.${guide.id}`)}
                  </p>
                  <div className="mt-6 flex items-center justify-between gap-4 border-t border-line pt-5">
                    <div className="flex gap-4 text-sm text-ink-3">
                      <span className="flex items-center gap-1.5"><Clock size={14} /> {readMins(guide.readTime)}</span>
                      <span className="flex items-center gap-1.5"><BarChart size={14} /> {level(guide.difficulty)}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleSelectGuide(guide.title)}
                      className={buttonClass(selected ? "primary" : "secondary", "sm")}
                      aria-pressed={selected}
                    >
                      {selected ? t("guide.selected") : t("guide.choose")}
                    </button>
                  </div>
                </article>
              </Reveal>
            );
          })}
        </div>
      </Section>

      {/* Purchase */}
      <section ref={formRef} id="purchase-form" className="scroll-mt-24 px-3 sm:px-5">
        <div className="rounded-[2rem] border border-line bg-surface py-16 md:py-24">
          <Container className="grid gap-12 lg:grid-cols-12">
            <Reveal className="lg:col-span-7">
              <Eyebrow>{t("guide.buyLabel")}</Eyebrow>
              <h2 className="mt-4 font-display text-5xl leading-[1.05] text-ink md:text-6xl">{t("guide.buyTitle")}</h2>
              <p className="mt-3 text-[0.9375rem] text-ink-2">{t("guide.buySub")}</p>

              <form onSubmit={handleSubmit} className="mt-8 space-y-5">
                <div className="grid gap-5 md:grid-cols-2">
                  <div>
                    <label htmlFor="guide-name" className="field-label">{t("guide.name")}</label>
                    <input
                      id="guide-name"
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      autoComplete="name"
                      aria-invalid={Boolean(errors.name)}
                      className={`field ${errors.name ? "border-danger" : ""}`}
                    />
                    {errors.name && <p className="mt-1.5 text-sm text-danger">{tErr(errors.name)}</p>}
                  </div>
                  <div>
                    <label htmlFor="guide-phone" className="field-label">{t("guide.phone")}</label>
                    <input
                      id="guide-phone"
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      autoComplete="tel"
                      placeholder="+91"
                      dir="ltr"
                      aria-invalid={Boolean(errors.phone)}
                      className={`field ${errors.phone ? "border-danger" : ""}`}
                    />
                    {errors.phone && <p className="mt-1.5 text-sm text-danger">{tErr(errors.phone)}</p>}
                  </div>
                </div>

                <div>
                  <label htmlFor="guide-email" className="field-label">{t("guide.email")}</label>
                  <input
                    id="guide-email"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    autoComplete="email"
                    placeholder="you@example.com"
                    dir="ltr"
                    aria-invalid={Boolean(errors.email)}
                    className={`field ${errors.email ? "border-danger" : ""}`}
                  />
                  {errors.email && <p className="mt-1.5 text-sm text-danger">{tErr(errors.email)}</p>}
                </div>

                <div>
                  <label htmlFor="guide-country" className="field-label">{t("guide.country")}</label>
                  <select id="guide-country" name="country" value={formData.country} onChange={handleChange} className="field">
                    {["India", "United States", "United Kingdom", "Canada", "Australia", "Germany", "Other"].map((c) => (
                      <option key={c} value={c}>
                        {t("guide.countries")[c] || c}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="grid gap-5 md:grid-cols-2">
                  <div>
                    <label htmlFor="guide-gst" className="field-label">
                      {t("guide.gst")} {optional}
                    </label>
                    <input id="guide-gst" type="text" name="gst_number" value={formData.gst_number} onChange={handleChange} className="field" dir="ltr" />
                  </div>
                  <div>
                    <label htmlFor="guide-company" className="field-label">
                      {t("guide.company")} {optional}
                    </label>
                    <input id="guide-company" type="text" name="company_name" value={formData.company_name} onChange={handleChange} autoComplete="organization" className="field" />
                  </div>
                </div>

                <label htmlFor="terms-check" className="flex cursor-pointer items-start gap-3 pt-2 text-[0.9375rem] leading-relaxed text-ink-2">
                  <input
                    type="checkbox"
                    name="terms_accepted"
                    checked={formData.terms_accepted}
                    onChange={handleChange}
                    className="mt-1 h-4 w-4 shrink-0 accent-brand"
                    id="terms-check"
                  />
                  <span>
                    {t("guide.agree")}{" "}
                    <Link to="/terms" className="text-brand underline underline-offset-2" target="_blank">{t("guide.terms")}</Link>{" "}
                    {t("guide.and")}{" "}
                    <Link to="/refund-policy" className="text-brand underline underline-offset-2" target="_blank">{t("guide.refund")}</Link>.
                  </span>
                </label>
                {errors.terms_accepted && <p className="text-sm text-danger">{tErr(errors.terms_accepted)}</p>}

                {errors.submit && (
                  <div className="pop-in rounded-2xl border border-danger/30 bg-danger-soft p-4 text-sm text-danger" role="alert">
                    {tErr(errors.submit)}
                  </div>
                )}

                <button type="submit" disabled={loading} className={`${buttonClass("primary", "lg")} w-full`}>
                  {loading ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      {t("guide.redirecting")}
                    </>
                  ) : (
                    <>
                      <Lock size={16} />
                      {t("guide.pay", { amount: pricing ? `₹${formatPrice(pricing.total_price)}` : "" })}
                    </>
                  )}
                </button>
              </form>
            </Reveal>

            <aside className="lg:col-span-5">
              <Reveal delay={150} className="sticky top-28 rounded-3xl border border-line bg-paper p-6 md:p-7">
                <p className="text-sm text-ink-3">{t("guide.buying")}</p>
                <div className="mt-3 flex items-start gap-4">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-brand-soft text-brand">
                    <FileText size={22} />
                  </div>
                  <div key={selectedGuideTitle} className="pop-in">
                    <h3 className="font-semibold leading-snug text-ink">{selectedGuideTitle}</h3>
                    <p className="mt-0.5 text-sm text-ink-3">{t("guide.format")}</p>
                  </div>
                </div>

                {pricing ? (
                  <dl className="mt-6 space-y-2.5 border-t border-line pt-5 text-[0.9375rem]">
                    <div className="flex justify-between">
                      <dt className="text-ink-2">{t("guide.price")}</dt>
                      <dd className="text-ink">₹{formatPrice(pricing.base_price)}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-ink-2">{t("guide.gstLine", { percent: pricing.gst_percent })}</dt>
                      <dd className="text-ink">₹{formatPrice(pricing.gst_amount)}</dd>
                    </div>
                    <div className="flex items-baseline justify-between border-t border-line pt-4">
                      <dt className="font-medium text-ink">{t("guide.total")}</dt>
                      <dd className="text-2xl font-semibold tracking-tight text-ink">₹{formatPrice(pricing.total_price)}</dd>
                    </div>
                  </dl>
                ) : (
                  <p className="mt-6 border-t border-line pt-5 text-sm text-ink-3">{t("guide.loadingPrice")}</p>
                )}

                <div className="mt-6 border-t border-line pt-5">
                  <p className="text-sm text-ink-2">{t("guide.methods")}</p>
                  <p className="mt-2 flex items-center gap-2 text-xs text-ink-3">
                    <Shield size={14} /> {t("guide.secure")}
                  </p>
                </div>
              </Reveal>
            </aside>
          </Container>
        </div>
      </section>

      {/* FAQ */}
      <Section>
        <div className="grid gap-10 md:grid-cols-12">
          <Reveal className="md:col-span-4">
            <Eyebrow>{t("guide.faqLabel")}</Eyebrow>
            <h2 className="mt-4 font-display text-4xl leading-[1.1] text-ink md:text-5xl">{t("guide.faqTitle")}</h2>
          </Reveal>
          <div className="space-y-3 md:col-span-8">
            {faqs.map((faq, i) => (
              <Reveal key={faq.q} delay={i * 70}>
                <details className="group rounded-2xl border border-line bg-surface px-5 transition-colors open:border-brand/40">
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-4 py-5 text-[1.0625rem] font-medium text-ink">
                    {faq.q}
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-line transition-all duration-300 group-open:rotate-180 group-open:border-brand group-open:bg-brand group-open:text-on-brand">
                      <ChevronDown size={16} />
                    </span>
                  </summary>
                  <p className="pb-5 pe-10 text-[0.9375rem] leading-relaxed text-ink-2">{faq.a}</p>
                </details>
              </Reveal>
            ))}
          </div>
        </div>
      </Section>

      <Footer />
    </>
  );
};

export default GuidePage;
